import { request } from '../../data/api/request';
import { AnalyticsMapperUser } from '../../interfaces/user/user';

const ANALYTICS_EMAIL_KEY = 'analyticsEmail';
const MAX_ATTEMPTS = 3;
const RETRY_DELAY = 500;

export const getAnalyticsMapperUser = () => request<AnalyticsMapperUser>('get', '/analytics-mapper/user');

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The resolved email is cached for the tab session, because the embed is reloaded
 * on every analytic section and the request blocks the ThoughtSpot init(). It is
 * stored together with the platform email of its owner: there is no logout here
 * to clean it up, so another user in the same browser must not reuse it.
 */
const getCachedEmail = (platformEmail?: string): string | undefined => {
  try {
    const cached = sessionStorage.getItem(ANALYTICS_EMAIL_KEY);
    const { user, email }: { user?: string, email?: string } = cached ? JSON.parse(cached) : {};
    return user === platformEmail ? email : undefined;
  } catch (error) {
    return undefined;
  }
};

const setCachedEmail = (platformEmail: string | undefined, email: string) => {
  try {
    sessionStorage.setItem(ANALYTICS_EMAIL_KEY, JSON.stringify({ user: platformEmail, email }));
  } catch (error) {
    // the storage may not be available inside the iframe, resolving it again is enough
  }
};

/**
 * Resolves the email address the ThoughtSpot session has to be opened with. It is
 * the primary email address of the user and it is NOT the platform email the embed
 * is opened with, so the analytics-mapper service is the only place to get it.
 *
 * There is nothing to fall back to, ThoughtSpot would reject any other address, so
 * a transient failure is retried and it throws when the email is still not resolved.
 *
 * @param platformEmail - email address of the cookie, only used to scope the cache
 * to the logged in user
 */
export const getThoughtSpotUsername = async (platformEmail?: string): Promise<string> => {
  const cachedEmail = getCachedEmail(platformEmail);
  if (cachedEmail) {
    return cachedEmail;
  }

  let lastStatus: number | undefined;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      await delay(RETRY_DELAY);
    }

    const { status, data } = await getAnalyticsMapperUser();
    if (data?.email) {
      setCachedEmail(platformEmail, data.email);
      return data.email;
    }

    lastStatus = status;
    console.warn(`[analytics-mapper] attempt ${attempt} of ${MAX_ATTEMPTS} did not resolve the email, status ${status}`);

    // only a network or a server error can answer something different on a retry,
    // a rejected request or a user without mapping will answer the same
    if (status && status < 500) {
      break;
    }
  }

  throw new Error(`[analytics-mapper] the email could not be resolved, status ${lastStatus}`);
};
